import { AttachmentPicker } from "./AttachmentPicker";
import "./attachments.css";
import React, { useState, useRef } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { Send } from "lucide-react";
import { EmojiPickerButton } from "./EmojiPickerButton";

export const ReplyForm = ({ onSubmit, placeholder = "Escribe tu respuesta...", attachmentPolicy, policyError }) => {
  const theme = useTheme();
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [submitError, setSubmitError] = useState("");
  const hasMessage = Boolean(content.trim() || files.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);
  const isDark = theme.mode === "dark";

  const handleEmojiSelect = (emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent((prev) => prev + emoji);
      return;
    }
    const start = textarea.selectionStart ?? content.length;
    const end = textarea.selectionEnd ?? content.length;
    const newContent = content.substring(0, start) + emoji + content.substring(end);
    setContent(newContent);

    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + emoji.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasMessage || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");
      setProgress(0);
      if (files.length) await onSubmit(content, files, setProgress);
      else await onSubmit(content);
      setFiles([]);
      setContent("");
    } catch (err) {
      setSubmitError(err.message || "No se pudo publicar la respuesta.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: "32px" }}>
      <div
        style={{
          padding: "28px",
          backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
          borderRadius: "12px",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          position: "relative",
          overflow: "visible",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "#ECB23620",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ECB236",
            }}
          >
            <Send size={20} />
          </div>
          <h3
            style={{
              margin: 0,
              color: theme.colors.textColor || theme.colors.textColorNavbar,
              fontSize: "var(--font-size-md, 18px)",
              fontWeight: 600,
            }}
          >
            Responder
          </h3>
        </div>

        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          rows={5}
          style={{
            width: "100%",
            padding: "16px",
            borderRadius: "12px",
            border: `2px solid ${isDark ? "#334155" : "#e2e8f0"}`,
            backgroundColor: isDark ? "#0f1221" : (theme.colors.inputBackground || "#f8fafc"),
            color: theme.colors.textColor || theme.colors.textColorNavbar,
            fontSize: "var(--font-size-body, 16px)",
            resize: "vertical",
            outline: "none",
            transition: "border-color 0.2s",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 500,
            lineHeight: 1.6,
            minHeight: "120px",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#ECB236";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDark ? "#334155" : "#e2e8f0";
          }}
        />
        {(submitError || policyError) && <p role="alert">{submitError || policyError.message}</p>}
        <div className="forum-reply-actions">
          <div className="forum-reply-tools">
          <EmojiPickerButton onEmojiSelect={handleEmojiSelect} disabled={isSubmitting} />
          <AttachmentPicker files={files} onChange={setFiles} policy={attachmentPolicy} disabled={isSubmitting} progress={progress} showLimits={false} />
          </div>
          <button
            type="submit"
            disabled={!hasMessage || isSubmitting}
            style={{
              padding: "14px 32px",
              borderRadius: "10px",
              border: "none",
              backgroundColor: hasMessage && !isSubmitting ? "#ECB236" : "#ccc",
              color: hasMessage && !isSubmitting ? "#fff" : "#666",
              fontWeight: 700,
              cursor: hasMessage && !isSubmitting ? "pointer" : "not-allowed",
              fontSize: "var(--font-size-body, 16px)",
              boxShadow: hasMessage && !isSubmitting ? "0 4px 12px rgba(236, 178, 54, 0.3)" : "none",
              transition: "all 0.2s ease",
              opacity: isSubmitting ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (hasMessage && !isSubmitting) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(236, 178, 54, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = hasMessage && !isSubmitting ? "0 4px 12px rgba(236, 178, 54, 0.3)" : "none";
            }}
          >
            {isSubmitting ? "Publicando..." : "Publicar respuesta"}
          </button>
        </div>
      </div>
    </form>
  );
};
